import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { inputFields, university, Spacer, Buttons } from "../../components";
import { UniversityAction } from "../../store/actions";

import { Utils } from "../../config";

const labels = [
  { label: "Required", value: "Required" },
  { label: "Not required", value: "Not required" },
  { label: "Not specified", value: "Not specified" },
  {
    label: "Neither required nor recommended",
    value: "Neither required nor recommended",
  },
];

const Others = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const id = location.pathname.split("/")[3];

  const { universityAdmin } = useSelector((state) => state?.university);

  const [state, setState] = useState({
    admissions: {},
    finance: {},
    rankings: {},
    requirements: {},
    subjectRanking: {},
    eligibility: [],
    recLetter: [],
    howToApply: [],
  });

  useEffect(() => {
    if (universityAdmin?.others) {
      setState(universityAdmin.others);
    }
  }, [universityAdmin]);

  const handleInputChange = (category, field, value) => {
    setState((prevState) => ({
      ...prevState,
      [category]: {
        ...prevState[category],
        [field]: value,
      },
    }));
  };

  const handleSubmit = () => {
    const payload = {
      id: parseInt(id),
      page: "others",
      data: state,
    };
    dispatch(
      UniversityAction.editUniversity(payload, () => {
        dispatch(UniversityAction.getUniversityDetailsAdmin(id));
      })
    );
  };

  const renderFields = (category, fields) => (
    <div className="grid grid-cols-2 gap-5">
      {fields.map(({ title, field, type = "text", isDropdown }) => (
        <div key={field}>
          <university.UniInputLabel title={title} />
          <Spacer height={10} />
          {isDropdown ? (
            <inputFields.SelectDropdown
              value={state[category]?.[field]}
              items={labels}
              onChange={(e) => handleInputChange(category, field, e)}
            />
          ) : (
            <inputFields.DefaultInput
              value={state[category]?.[field] || ""}
              type={type}
              onChange={(e) =>
                handleInputChange(category, field, e.target.value)
              }
            />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {[
        {
          title: "Admissions",
          category: "admissions",
          fields: [
            {
              title: "Eligibility domestic link",
              field: "eligibilityDomesticLink",
            },
            {
              title: "Eligibility international link",
              field: "eligibilityInternationalLink",
            },
            {
              title: "How to apply domestic link",
              field: "howToApplyDomesticLink",
            },
            {
              title: "How to apply international link",
              field: "howToApplyInternationLink",
            },
          ],
        },
        {
          title: "Finance",
          category: "finance",
          fields: [
            { title: "On-campus accommodation cost", field: "onAccCost" },
            { title: "Off-campus accommodation cost", field: "offAccCost" },
            { title: "Food cost", field: "foodCost" },
            { title: "Transport cost", field: "transportCost" },
            { title: "Entertainment cost", field: "entertainmentCost" },
          ],
        },
        {
          title: "Rankings",
          category: "rankings",
          fields: [
            { title: "QS", field: "QS" },
            { title: "QS INT", field: "QSINT", type: "number" },
            { title: "TIMES", field: "TIMES" },
            { title: "TIMES INT", field: "TIMESINT", type: "number" },
            { title: "CWUR", field: "CWUR" },
          ],
        },
        {
          title: "Requirements",
          category: "requirements",
          fields: [
            { title: "IELTS", field: "IELTS", isDropdown: true },
            { title: "TOEFL", field: "TOEFL", isDropdown: true },
            { title: "SAT/ACT", field: "SATorACT", isDropdown: true },
          ],
        },
        {
          title: "Subject Rankings",
          category: "subjectRanking",
          fields: [
            { title: "Arts & Humanities", field: "artsAndHumanities" },
            { title: "Archaeology", field: "archaeology" },
            { title: "Architecture", field: "architecture" },
            { title: "Art Design", field: "artDesign" },
            { title: "History of Art", field: "historyofArt" },
            { title: "Classics", field: "classics" },
            { title: "English Language", field: "englishLanguage" },
            { title: "History", field: "history" },
            { title: "Linguistics", field: "linguistics" },
            { title: "Music", field: "music" },
            { title: "Modern Language", field: "modernLanguage" },
            { title: "Performing Arts", field: "performingArts" },
            { title: "Philosophy", field: "philosophy" },
            { title: "Theology", field: "theology" },
            { title: "Engineering Technology", field: "engineeringTechnology" },
            { title: "Chemical Engineering", field: "chemicalEngineering" },
            { title: "Computer Science", field: "computerScience" },
            { title: "Civil Engineering", field: "civilEngineering" },
            { title: "Electrical Engineering", field: "electricalEngineering" },
            { title: "Mechanical Engineering", field: "mechanicalEngineering" },
            { title: "Data Science", field: "dataScience" },
            { title: "Mineral Engineering", field: "mineralEngineering" },
            { title: "Petroleum Engineering", field: "petroleumEngineering" },
            { title: "Life Sciences Medicine", field: "lifeSciencesMedicine" },
            { title: "Agriculture", field: "agriculture" },
            { title: "Anatomy", field: "anatomy" },
            { title: "Biological Sciences", field: "biologicalSciences" },
            { title: "Dentistry", field: "dentistry" },
            { title: "Medicine", field: "medicine" },
            { title: "Nursing", field: "nursing" },
            { title: "Pharmacy", field: "pharmacy" },
            { title: "Psychology", field: "psychology" },
            { title: "Veterinary Sciences", field: "veterinarySciences" },
            { title: "Natural Sciences", field: "naturalSciences" },
            { title: "Chemistry", field: "chemistry" },
            {
              title: "Earth and Marine Sciences",
              field: "earthandMarineSciences",
            },
            { title: "Environmental Sciences", field: "environmentalSciences" },
            { title: "Geography", field: "geography" },
            { title: "Geology", field: "geology" },
            { title: "Geophysics", field: "geophysics" },
            { title: "Materials Science", field: "materialsScience" },
            { title: "Mathematics", field: "mathematics" },
            { title: "Physics", field: "physics" },
            {
              title: "Social Sciences Management",
              field: "socialSciencesManagement",
            },
            { title: "Accounting", field: "accounting" },
            { title: "Anthropology", field: "anthropology" },
            { title: "Business", field: "business" },
            { title: "Communication", field: "communication" },
            { title: "Developmental Studies", field: "developmentalStudies" },
            { title: "Economics Econometrics", field: "economicsEconometrics" },
            { title: "Education", field: "education" },
            { title: "Hospitality", field: "hospitality" },
            { title: "Law", field: "law" },
            { title: "Library", field: "library" },
            { title: "Politics", field: "politics" },
            { title: "SocialPolicy", field: "socialPolicy" },
            { title: "Sociology", field: "sociology" },
            { title: "Sports Related Subject", field: "sportsRelatedSubject" },
            { title: "Marketing", field: "marketing" },
            { title: "Statistics", field: "statistics" },
          ],
        },
      ].map(({ title, category, fields }) => (
        <div key={category}>
          <div className="text-main text-[1.2em] font-semibold">{title}</div>
          {renderFields(category, fields)}
        </div>
      ))}

      <div>
        <div className="text-main text-[1.2em] font-semibold">
          Recommendation Letter
        </div>
        <Spacer height={10} />
        <inputFields.SelectDropdownTags
          items={Utils.filterArray(state?.recLetter)}
          value={state?.recLetter}
          onChange={(e) => {
            setState({ ...state, recLetter: e });
          }}
        />
      </div>
      <div>
        <div className="text-main text-[1.2em] font-semibold">Eligibility</div>
        <Spacer height={10} />
        <inputFields.SelectDropdownTags
          items={Utils.filterArray(state?.eligibility)}
          value={state?.eligibility}
          onChange={(e) => {
            setState({ ...state, eligibility: e });
          }}
        />
      </div>
      <div>
        <div className="text-main text-[1.2em] font-semibold">How to apply</div>
        <Spacer height={10} />
        <inputFields.SelectDropdownTags
          items={Utils.filterArray(state?.howToApply)}
          value={state?.howToApply}
          onChange={(e) => {
            setState({ ...state, howToApply: e });
          }}
        />
      </div>
      <div className="flex w-full justify-end">
        <Buttons.DefaultButton onClick={handleSubmit}>
          Save Changes
        </Buttons.DefaultButton>
      </div>
      <Spacer height={40} />
    </div>
  );
};

export default Others;
